import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, signal, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatListItem } from '@angular/material/list';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { SideNavItem, SideNavItemViewModel } from '../../models/sidenav-item.interface';
import { UserFavoritesService } from '../../services/user-favorites.service';
import { AuthService } from '../../services/auth/auth.service';
import { isDefined, isUndefined } from '../../util';

@Component({
  imports: [
    CommonModule,
    MatListItem,
    TranslateModule
  ],
  selector: 'app-sidenav-item',
  standalone: true,
  styleUrls: ['sidenav-item.component.scss'],
  templateUrl: 'sidenav-item.component.html',
  encapsulation: ViewEncapsulation.None
})
export class SideNavItemComponent implements OnChanges, OnInit {
  @Input() public item: SideNavItem;
  @Input() public depth: number;
  @Input() public isInFavorites: boolean = false; // To know if it's in the Favorites section

  public vm: SideNavItemViewModel;

  private destroyRef: DestroyRef;

  public isFavorite = signal<boolean>(false);
  public isTogglingFavorite = signal<boolean>(false); // Flag to show spinner
  private currentUserId: string = '';

  // Computed property to check if it's My Dashboard
  public get isMyDashboard(): boolean {
    return this.item?.route === 'master-dashboard';
  }

  public constructor(private router: Router,
                     private favoritesService: UserFavoritesService,
                     private authService: AuthService) {
    if (isUndefined(this.depth)) {
      this.depth = 0;
    }
    this.destroyRef = inject(DestroyRef);
  }

  public ngOnInit(): void {
    this.vm = {
      isActive: signal(false)
    };

    this.checkRouteActive();
    this.subscribeRouteEvents();

    // Subscribe to auth to get userId
    this.authService.userProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user && user.sub) {
          this.currentUserId = user.sub;
          this.favoritesService.loadFavorites(this.currentUserId).subscribe();
        }
      });

    // Subscribe to favorites changes
    this.favoritesService.favorites$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateFavoriteStatus();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (isDefined(this.vm) && isDefined(changes['item']) && !changes['item'].firstChange &&
        changes['item'].currentValue !== changes['item'].previousValue) {
      this.checkRouteActive();
      this.updateFavoriteStatus();
    }
  }

  private checkRouteActive(): void {
    if (this.vm) {
      this.vm.isActive.set(
        this.item?.route ? this.router.url === `/${this.item.route}` : false
      );
    }
  }

  private subscribeRouteEvents(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd): void => {
        if (this.vm && this.item) {
          this.vm.isActive.set(event.url === `/${this.item.route}`);
        }
      });
  }

  public onItemSelected(): void {
    const item = this.item;
    if (!item) return;

    // Locked items redirect to Settings > Subscription
    if (item.isLocked) {
      this.router.navigate(['settings'], { queryParams: { tab: 'subscription' } });
      return;
    }

    if (item.route.startsWith('http')) {
      window.open(item.route, '_blank');
    } else {
      this.router.navigate([item.route]);
    }
  }

  onToggleFavorite(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.item || !this.currentUserId) {
      console.warn('Cannot toggle favorite: missing item or userId');
      return;
    }

    // Prevent multiple clicks
    if (this.isTogglingFavorite()) {
      return;
    }

    const item = this.item;
    const currentStatus = this.isFavorite();

    // ? OPTIMISTIC update: Change visual state immediately
    this.isFavorite.set(!currentStatus);
    this.isTogglingFavorite.set(true);

    if (currentStatus) {
      // Remove from favorites
      this.favoritesService.removeFavorite(this.currentUserId, item.route).subscribe({
        next: () => {
          console.log('? Removed from favorites:', item.displayName);
          this.isTogglingFavorite.set(false);
        },
        error: (error) => {
          console.error('? Error removing favorite:', error);
          // Revert optimistic change on error
          this.isFavorite.set(currentStatus);
          this.isTogglingFavorite.set(false);
        }
      });
    } else {
      // Add to favorites
      this.favoritesService.addFavorite(this.currentUserId, item).subscribe({
        next: () => {
          console.log('? Added to favorites:', item.displayName);
          this.isTogglingFavorite.set(false);
        },
        error: (error) => {
          console.error('? Error adding favorite:', error);
          // Revert optimistic change on error
          this.isFavorite.set(currentStatus);
          this.isTogglingFavorite.set(false);
        }
      });
    }
  }

  private updateFavoriteStatus(): void {
    if (this.item && this.item.route) {
      const isFav = this.favoritesService.isFavorite(this.item.route);
      this.isFavorite.set(isFav);
    }
  }
}
